import { useEffect, useRef, useState } from "react";
import type { SocialPostConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { useSpeaking } from "../lib/useSpeaking";
import { tx, txr } from "../lib/tx";

/**
 * The victim oversharing online — a 5-star Google review of a restaurant, or an
 * Instagram vacation post. A phone-framed overlay shows the post being composed;
 * the learner taps "Post" and the content goes public (the seed an attacker
 * harvests two scenes later). Records a soft `overshared_publicly`. Cinematic
 * with a single decisive tap; ends on Continue.
 */
export function SocialPost({
  config,
  onPosted,
  onContinue,
}: {
  config: SocialPostConfig;
  onPosted: () => void;
  onContinue: () => void;
}) {
  const [posted, setPosted] = useState(false);
  const isReview = config.platform === "google_review";
  // For a narrated review, withhold the rating until ~"five stars" is spoken so it
  // pops in on cue (the camera has already pushed onto the table). Everything else
  // is revealed immediately.
  const [revealed, setRevealed] = useState(!(isReview && !!config.line));
  const mounted = useRef(true);
  const speaking = useSpeaking();

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.line) void audio.prefetchSpeech(config.line, { voice: config.voiceId || "coral", lang: config.language || "en-US" });
    mounted.current = true;
    const timers: number[] = [];
    if (config.line) {
      timers.push(
        window.setTimeout(
          () => mounted.current && audio.speak(config.line!, { voice: config.voiceId || "coral", lang: config.language || "en-US" }),
          160
        )
      );
    }
    if (!revealed) {
      timers.push(window.setTimeout(() => mounted.current && setRevealed(true), config.revealRatingMs ?? 2600));
    }
    return () => {
      mounted.current = false;
      audio.cancelSpeech();
      timers.forEach((t) => window.clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stars = Math.max(0, Math.min(5, config.rating ?? 5));

  const post = () => {
    if (posted) return;
    setPosted(true);
    onPosted();
  };

  return (
    <div className="social">
      <div className={`social-phone ${isReview ? "review" : "insta"}`}>
        {/* app header */}
        <div className="social-head">
          {isReview ? (
            <>
              <span className="social-g">
                {txr("<0>G</0>oogle", {}, [<i className="social-g-b" />])}
              </span>
              <span className="social-head-sub">{tx("Write a review")}</span>
            </>
          ) : (
            <>
              <span className="social-ig">{tx("Instagram")}</span>
              <span className="social-head-sub">{tx("New post")}</span>
            </>
          )}
        </div>

        <div className="social-author">
          <span className="social-avatar">
            {config.avatarUrl ? <img src={config.avatarUrl} alt="" /> : config.authorName.slice(0, 1)}
          </span>
          <span className="social-author-text">
            <span className="social-author-name">{config.authorName}</span>
            <span className="social-author-handle">
              {config.authorHandle ?? (isReview ? tx("Posting publicly") : tx("Public · Followers"))}
            </span>
          </span>
        </div>

        {isReview && (
          <div className="social-review-target">
            <span className="social-pin" aria-hidden>{"\u{1F4CD}"}</span>
            <span>{config.business ?? tx("Restaurant")}</span>
          </div>
        )}
        {!isReview && config.location && (
          <div className="social-review-target">
            <span className="social-pin" aria-hidden>{"\u{1F4CD}"}</span>
            <span>{config.location}</span>
          </div>
        )}

        {isReview && (
          <div className={`social-stars ${revealed ? "in" : "pending"}`} aria-label={tx("{stars} of 5 stars", { stars })}>
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i} className={`social-star ${revealed && i < stars ? "on" : ""}`}>{"\u2605"}</span>
            ))}
          </div>
        )}

        {config.photos && config.photos.length > 0 && (
          <div className={`social-photos n${Math.min(config.photos.length, 4)}`}>
            {config.photos.slice(0, 4).map((p, i) => (
              <span key={i} className="social-photo">
                <img src={p} alt="" />
              </span>
            ))}
          </div>
        )}

        <div className="social-caption">{config.caption}</div>

        {!posted ? (
          <button
            type="button"
            className={`social-post-btn ${revealed ? "" : "waiting"}`}
            onClick={post}
            disabled={!revealed}
          >
            {config.postLabel ?? (isReview ? tx("Post review") : tx("Share"))}
          </button>
        ) : (
          <div className="social-posted">
            <div className="social-posted-row">
              <span className="social-posted-tick" aria-hidden>{"\u2713"}</span>
              {config.postedToast ?? tx("Posted — visible to everyone")}
            </div>
            <button
              type="button"
              className={`btn social-continue ${speaking ? "speaking" : ""}`}
              onClick={onContinue}
              disabled={speaking}
            >
              {config.continueLabel ?? tx("Continue")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
